// Cliente da API: conta e pagamento.
//
// Mesmo desenho do 38 Brasil -- a conta guarda um token no aparelho, e a mesma
// conta libera o premium no computador e no celular. Sem servidor no ar, tudo
// aqui falha com mensagem clara em vez de fingir que deu certo.

const TOKEN_KEY = 'basquete82.conta';

/**
 * Prefixo em que o jogo foi publicado.
 *
 * Local e '/', e no servidor e '/82basquete/' -- o mesmo dominio hospeda
 * outros sites, entao pedir /api/... na raiz cairia no vizinho. O Vite
 * grava esse valor no build (--base), e nao ha nada para configurar em
 * dois lugares.
 */
const BASE = (import.meta.env?.BASE_URL || '/').replace(/\/$/, '');
export const apiUrl = (caminho) => BASE + caminho;

export function readAccount() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * SESSAO DERRUBADA: quem quer saber se inscreve aqui.
 *
 * Um `Set` de funcoes, e nao um unico callback: o App precisa limpar o estado
 * e mostrar o aviso, e um dia outra tela pode precisar reagir tambem. Com um
 * callback so, o segundo interessado apagaria o primeiro sem ninguem notar.
 */
const ouvintes = new Set();

export function aoEncerrarSessao(fn) {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
}

/**
 * Limpa a conta deste aparelho e avisa quem estiver ouvindo.
 *
 * ⚠️ NAO toca na carreira. Ela vive em outra chave do localStorage e e do
 * APARELHO, nao da conta -- perder a sessao nao pode custar dez temporadas.
 */
function encerrarSessao() {
  if (!readAccount()) return;          // ja estava deslogado; nao avisa duas vezes
  writeAccount(null);
  for (const fn of ouvintes) {
    try { fn(); } catch { /* um ouvinte quebrado nao pode travar os outros */ }
  }
}

export function writeAccount(account) {
  try {
    if (account) localStorage.setItem(TOKEN_KEY, JSON.stringify(account));
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* armazenamento bloqueado */ }
  return account;
}

async function call(path, { method = 'GET', body, token } = {}) {
  let res;
  try {
    res = await fetch(apiUrl(path), {
      method,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error('Sem conexao com o servidor.');
  }
  let data = {};
  try { data = await res.json(); } catch { /* resposta sem corpo */ }
  if (!res.ok) {
    /* ⚠️ 401 COM TOKEN = a sessao foi derrubada em outro aparelho.
       O servidor guarda UM token por conta: entrar no celular invalida o
       token do computador na hora. Quem descobre isso e a primeira chamada
       que falha, e ela pode ser o envio automatico do save na nuvem -- ou
       seja, no meio de uma partida, sem ninguem ter clicado em nada.

       Sem tratar aqui, a pessoa ficaria com a tela dizendo "logado" e todas
       as acoes falhando em silencio. Entao a sessao morta e limpa no ato e
       quem estiver ouvindo e avisado.

       ⚠️ So vale quando HAVIA token. O 401 de senha errada no login nao tem
       token nenhum, e limpar a conta ali derrubaria quem so errou a senha. */
    if (res.status === 401 && token) encerrarSessao();
    const erro = new Error(data.error || `Falha na requisicao (${res.status}).`);
    erro.status = res.status;
    // O 409 do save na nuvem carrega o save remoto: quem chamou precisa dele
    // para mostrar as duas carreiras e deixar o usuario escolher.
    if (data.conflict) { erro.conflict = true; erro.payload = data; }
    throw erro;
  }
  return data;
}

export const createAccount = (email, password, extras = {}) =>
  call('/api/account/create', { method: 'POST', body: { email, password, ...extras } });

export const loginAccount = (email, password) =>
  call('/api/account/login', { method: 'POST', body: { email, password } });

export const fetchAccount = (token) => call('/api/account/me', { token });

/** Nome e WhatsApp depois do cadastro. O e-mail nao muda: ele e a conta. */
export const saveProfile = (token, dados) =>
  call('/api/account/profile', { method: 'POST', token, body: dados });

/** Trocar a propria senha. Pede a atual de proposito. */
export const changePassword = (token, atual, nova) =>
  call('/api/account/password', { method: 'POST', token, body: { atual, nova } });

/** Relato de bug. Funciona sem conta: quem esta travado pode nao conseguir entrar. */
export const reportBug = (token, dados) =>
  call('/api/bug', { method: 'POST', token, body: dados });

/** Voto na enquete do dono. */
export const votarEnquete = (token, opcao) =>
  call('/api/enquete', { method: 'POST', token, body: { opcao } });

/** Gera a cobranca Pix. Devolve { paymentId, qrImage, copyPaste, amount }. */
export const createUnlockPayment = (token) =>
  call('/api/unlock/create', { method: 'POST', token });

/** Pergunta ao servidor se o pagamento caiu. */
export const checkUnlockPayment = (token, paymentId) =>
  call(`/api/unlock/check?paymentId=${encodeURIComponent(paymentId)}`, { token });

export const pushCloudSave = (token, save, { baseVersion = 0, force = false } = {}) =>
  call('/api/cloud-save', { method: 'PUT', token, body: { save, baseVersion, force } });

export const pullCloudSave = (token) => call('/api/cloud-save', { token });

export const fetchCeoStats = (token) => call('/api/ceo/stats', { token });
export const ceoGrant = (token, email, on) =>
  call('/api/ceo/grant', { method: 'POST', token, body: { email, on } });
export const ceoConfig = (token, config) =>
  call('/api/ceo/config', { method: 'POST', token, body: config });
export const ceoAviso = (token, aviso) =>
  call('/api/ceo/aviso', { method: 'POST', token, body: aviso });
export const ceoBuscar = (token, termo) =>
  call('/api/ceo/buscar', { method: 'POST', token, body: { termo } });
export const ceoSenha = (token, email, nova) =>
  call('/api/ceo/senha', { method: 'POST', token, body: { email, nova } });
export const ceoTelaInicial = (token, dados) =>
  call('/api/ceo/tela-inicial', { method: 'POST', token, body: dados });
export const ceoPromo = (token, dados) =>
  call('/api/ceo/promo', { method: 'POST', token, body: dados });
export const ceoEnquete = (token, dados) =>
  call('/api/ceo/enquete', { method: 'POST', token, body: dados });
export const ceoBug = (token, id, resolvido) =>
  call('/api/ceo/bug', { method: 'POST', token, body: { id, resolvido } });
export const ceoZerarReceita = (token) =>
  call('/api/ceo/zerar-receita', { method: 'POST', token });

/** Configuracao publicada e recado do dono. Rota aberta, lida no boot. */
export const fetchGameConfig = (token) => call('/api/config', { token });
